"use client"

import { createContext, useCallback, useEffect, useMemo, useRef } from "react"
import { getSupabaseBrowserClient } from "@/utils/supabase/client"
import { normalizeOfferRow, toFiniteNumber } from "@/lib/offers/offer-utils"
import type {
  OfferRow,
  OfferSlot,
  OfferStatus,
  OfferDeclineReason,
  ListingOffersAggregate,
} from "@/lib/offers/offer-types"

export interface OfferRealtimeContextValue {
  subscribeOffer(id: string, initial?: Partial<OfferSlot>): void
  unsubscribeOffer(id: string): void
  subscribeListing(listingId: string, initial?: Partial<ListingOffersAggregate>): void
  unsubscribeListing(listingId: string): void
  patchOffer(id: string, partial: Partial<OfferSlot>): void
  getOfferSnapshot(id: string): OfferSlot | null
  getListingSnapshot(listingId: string): ListingOffersAggregate | null
  addOfferListener(id: string, fn: () => void): () => void
  addListingListener(listingId: string, fn: () => void): () => void
  refetchOffer(id: string): Promise<void>
  refetchListing(listingId: string): Promise<void>
  unreadNotificationCount: () => number
  addNotificationListener(fn: () => void): () => void
  actionCount: () => number
  addActionCountListener(fn: () => void): () => void
  addOfferListListener(fn: () => void): () => void
  markNotificationsRead(ids: string[]): Promise<void>
  markAllNotificationsRead: () => Promise<void>
}

export const OfferRealtimeContext = createContext<OfferRealtimeContextValue | null>(null)

function defaultOfferSlot(initial?: Partial<OfferSlot>): OfferSlot {
  return {
    amount: 0,
    status: "pending",
    expiresAt: new Date().toISOString(),
    paymentDeadline: null,
    eventVersion: 0,
    declineReason: null,
    parentOfferId: null,
    settledAt: null,
    ...initial,
  }
}

function defaultListingAggregate(
  listingId: string,
  initial?: Partial<ListingOffersAggregate>
): ListingOffersAggregate {
  return {
    listingId,
    pendingCount: initial?.pendingCount ?? 0,
    highestPending: initial?.highestPending ?? null,
  }
}

function rowToSlotPatch(row: OfferRow): Partial<OfferSlot> {
  return {
    amount: row.amount,
    status: row.status,
    expiresAt: row.expires_at,
    paymentDeadline: row.payment_deadline,
    eventVersion: row.event_version,
    declineReason: row.decline_reason as OfferDeclineReason | null,
    parentOfferId: row.parent_offer_id,
    settledAt: row.settled_at,
  }
}

interface ProviderProps {
  userId: string | null
  children: React.ReactNode
}

export function OfferRealtimeProvider({ userId, children }: ProviderProps) {
  const offersRef = useRef(new Map<string, OfferSlot>())
  const listingsRef = useRef(new Map<string, ListingOffersAggregate>())
  const offerListenersRef = useRef(new Map<string, Set<() => void>>())
  const listingListenersRef = useRef(new Map<string, Set<() => void>>())
  const offerCountRef = useRef(new Map<string, number>())
  const listingCountRef = useRef(new Map<string, number>())
  const notificationListenersRef = useRef(new Set<() => void>())
  const actionCountListenersRef = useRef(new Set<() => void>())
  const offerListListenersRef = useRef(new Set<() => void>())
  const unreadCountRef = useRef(0)
  const actionCountRef = useRef(0)

  const notifyOffer = useCallback((id: string) => {
    offerListenersRef.current.get(id)?.forEach((fn) => fn())
  }, [])
  const notifyListing = useCallback((listingId: string) => {
    listingListenersRef.current.get(listingId)?.forEach((fn) => fn())
  }, [])
  const notifyNotifications = useCallback(() => {
    notificationListenersRef.current.forEach((fn) => fn())
  }, [])
  const notifyActionCount = useCallback(() => {
    actionCountListenersRef.current.forEach((fn) => fn())
  }, [])
  const notifyOfferList = useCallback(() => {
    offerListListenersRef.current.forEach((fn) => fn())
  }, [])

  const patchOffer = useCallback(
    (id: string, partial: Partial<OfferSlot>) => {
      const current = offersRef.current.get(id)
      if (!current) {
        offersRef.current.set(id, defaultOfferSlot(partial))
        notifyOffer(id)
        return
      }
      const incomingVersion = partial.eventVersion ?? current.eventVersion
      if (incomingVersion < current.eventVersion) return
      const next = { ...current, ...partial, eventVersion: incomingVersion }
      offersRef.current.set(id, next)
      notifyOffer(id)
    },
    [notifyOffer]
  )

  const subscribeOffer = useCallback(
    (id: string, initial?: Partial<OfferSlot>) => {
      const prev = offerCountRef.current.get(id) ?? 0
      const count = prev + 1
      offerCountRef.current.set(id, count)
      if (prev === 0) {
        offersRef.current.set(id, defaultOfferSlot(initial))
        notifyOffer(id)
      } else if (initial) {
        const cur = offersRef.current.get(id)!
        if ((initial.eventVersion ?? 0) > cur.eventVersion) {
          offersRef.current.set(id, { ...cur, ...initial })
          notifyOffer(id)
        }
      }
    },
    [notifyOffer]
  )

  const unsubscribeOffer = useCallback((id: string) => {
    const count = offerCountRef.current.get(id) ?? 0
    if (count <= 1) {
      offerCountRef.current.delete(id)
      offersRef.current.delete(id)
    } else {
      offerCountRef.current.set(id, count - 1)
    }
  }, [])

  const subscribeListing = useCallback(
    (listingId: string, initial?: Partial<ListingOffersAggregate>) => {
      const count = (listingCountRef.current.get(listingId) ?? 0) + 1
      listingCountRef.current.set(listingId, count)
      if (!listingsRef.current.has(listingId)) {
        listingsRef.current.set(listingId, defaultListingAggregate(listingId, initial))
      } else if (initial) {
        const cur = listingsRef.current.get(listingId)!
        listingsRef.current.set(listingId, { ...cur, ...initial })
        notifyListing(listingId)
      }
    },
    [notifyListing]
  )

  const unsubscribeListing = useCallback((listingId: string) => {
    const count = listingCountRef.current.get(listingId) ?? 0
    if (count <= 1) {
      listingCountRef.current.delete(listingId)
    } else {
      listingCountRef.current.set(listingId, count - 1)
    }
  }, [])

  const getOfferSnapshot = useCallback((id: string) => offersRef.current.get(id) ?? null, [])
  const getListingSnapshot = useCallback(
    (listingId: string) => listingsRef.current.get(listingId) ?? null,
    []
  )

  const addOfferListener = useCallback((id: string, fn: () => void) => {
    let set = offerListenersRef.current.get(id)
    if (!set) {
      set = new Set()
      offerListenersRef.current.set(id, set)
    }
    set.add(fn)
    return () => {
      set!.delete(fn)
      if (set!.size === 0) offerListenersRef.current.delete(id)
    }
  }, [])

  const addListingListener = useCallback((listingId: string, fn: () => void) => {
    let set = listingListenersRef.current.get(listingId)
    if (!set) {
      set = new Set()
      listingListenersRef.current.set(listingId, set)
    }
    set.add(fn)
    return () => {
      set!.delete(fn)
      if (set!.size === 0) listingListenersRef.current.delete(listingId)
    }
  }, [])

  const addNotificationListener = useCallback((fn: () => void) => {
    notificationListenersRef.current.add(fn)
    return () => {
      notificationListenersRef.current.delete(fn)
    }
  }, [])

  const refetchOffer = useCallback(
    async (id: string) => {
      try {
        const res = await fetch(`/api/offers/${encodeURIComponent(id)}`, { credentials: "include" })
        if (!res.ok) return
        const json = await res.json()
        const offer = json?.offer
        if (offer) {
          const normalized = normalizeOfferRow(offer)
          if (normalized) patchOffer(id, rowToSlotPatch(normalized))
        }
      } catch {
        /* network noise */
      }
    },
    [patchOffer]
  )

  const refetchListing = useCallback(
    async (listingId: string) => {
      try {
        const res = await fetch(`/api/listings/${encodeURIComponent(listingId)}/offers`, {
          credentials: "include",
        })
        if (!res.ok) return
        const json = await res.json()
        const stats = json?.stats
        if (stats) {
          const cur = listingsRef.current.get(listingId)
          listingsRef.current.set(listingId, {
            listingId,
            pendingCount: typeof stats.pending === "number" ? stats.pending : cur?.pendingCount ?? 0,
            highestPending:
              typeof stats.highest_pending === "number" ? stats.highest_pending : cur?.highestPending ?? null,
          })
          notifyListing(listingId)
        }
      } catch {
        /* network noise */
      }
    },
    [notifyListing]
  )

  const syncUnreadFromServer = useCallback(async () => {
    if (!userId) return
    const c = await fetchUnreadCount(userId)
    unreadCountRef.current = c
    notifyNotifications()
  }, [userId, notifyNotifications])

  const syncActionCountFromServer = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch("/api/offers/action-count", { credentials: "include" })
      if (!res.ok) return
      const json = (await res.json()) as { actionCount?: number }
      actionCountRef.current =
        typeof json.actionCount === "number" ? Math.max(0, json.actionCount) : 0
      notifyActionCount()
    } catch {
      /* swallow */
    }
  }, [userId, notifyActionCount])

  const markNotificationsRead = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return
      try {
        const res = await fetch("/api/offers/notifications", {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        })
        if (!res.ok) return
        await syncUnreadFromServer()
      } catch {
        /* swallow */
      }
    },
    [syncUnreadFromServer]
  )

  const markAllNotificationsRead = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch("/api/offers/notifications", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all: true }),
      })
      if (!res.ok) return
      await syncUnreadFromServer()
    } catch {
      /* swallow */
    }
  }, [userId, syncUnreadFromServer])

  const unreadNotificationCount = useCallback(() => unreadCountRef.current, [])
  const actionCount = useCallback(() => actionCountRef.current, [])

  const addActionCountListener = useCallback((fn: () => void) => {
    actionCountListenersRef.current.add(fn)
    return () => {
      actionCountListenersRef.current.delete(fn)
    }
  }, [])

  const addOfferListListener = useCallback((fn: () => void) => {
    offerListListenersRef.current.add(fn)
    return () => {
      offerListListenersRef.current.delete(fn)
    }
  }, [])

  // ---------- Realtime subscriptions ----------
  useEffect(() => {
    if (!userId) return
    const supabase = getSupabaseBrowserClient()

    const handleOfferChange = (
      eventType: "INSERT" | "UPDATE" | "DELETE",
      record: Record<string, unknown> | undefined,
      old: Record<string, unknown> | undefined
    ) => {
      const row = record ?? old
      if (!row) return
      const normalized = normalizeOfferRow(row)
      if (!normalized) return
      const id = normalized.id
      if (offerCountRef.current.has(id)) {
        if (eventType === "DELETE") {
          offersRef.current.delete(id)
        } else {
          patchOffer(id, rowToSlotPatch(normalized))
        }
      }
      if (listingCountRef.current.has(normalized.listing_id)) {
        void refetchListing(normalized.listing_id)
      }
      void syncActionCountFromServer()
      notifyOfferList()
    }

    const offerChannel = supabase
      .channel(`offers:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "offers", filter: `buyer_id=eq.${userId}` },
        (payload) =>
          handleOfferChange(
            payload.eventType as "INSERT" | "UPDATE" | "DELETE",
            payload.new as Record<string, unknown>,
            payload.old as Record<string, unknown>
          )
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "offers", filter: `seller_id=eq.${userId}` },
        (payload) =>
          handleOfferChange(
            payload.eventType as "INSERT" | "UPDATE" | "DELETE",
            payload.new as Record<string, unknown>,
            payload.old as Record<string, unknown>
          )
      )
      .subscribe()

    const notifChannel = supabase
      .channel(`offer-notifs:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "offer_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          unreadCountRef.current += 1
          notifyNotifications()
          void syncActionCountFromServer()
          notifyOfferList()
        }
      )
      .subscribe()

    const listingChannel = supabase
      .channel(`listings-offer-aggs:${userId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "listings" },
        (payload) => {
          const row = payload.new as Record<string, unknown>
          const id = typeof row.id === "string" ? row.id : null
          if (!id || !listingCountRef.current.has(id)) return
          const cur = listingsRef.current.get(id)
          const pendingCount = toFiniteNumber(row.offers_pending_count) ?? cur?.pendingCount ?? 0
          const highestPending = toFiniteNumber(row.offers_highest_pending)
          listingsRef.current.set(id, {
            listingId: id,
            pendingCount,
            highestPending,
          })
          notifyListing(id)
        }
      )
      .subscribe()

    void fetchUnreadCount(userId).then((c) => {
      unreadCountRef.current = c
      notifyNotifications()
    })
    void syncActionCountFromServer()

    return () => {
      void supabase.removeChannel(offerChannel)
      void supabase.removeChannel(notifChannel)
      void supabase.removeChannel(listingChannel)
    }
  }, [userId, patchOffer, notifyListing, notifyNotifications, refetchListing, syncActionCountFromServer, notifyOfferList])

  const value = useMemo<OfferRealtimeContextValue>(
    () => ({
      subscribeOffer,
      unsubscribeOffer,
      subscribeListing,
      unsubscribeListing,
      patchOffer,
      getOfferSnapshot,
      getListingSnapshot,
      addOfferListener,
      addListingListener,
      refetchOffer,
      refetchListing,
      unreadNotificationCount,
      addNotificationListener,
      actionCount,
      addActionCountListener,
      addOfferListListener,
      markNotificationsRead,
      markAllNotificationsRead,
    }),
    [
      subscribeOffer,
      unsubscribeOffer,
      subscribeListing,
      unsubscribeListing,
      patchOffer,
      getOfferSnapshot,
      getListingSnapshot,
      addOfferListener,
      addListingListener,
      refetchOffer,
      refetchListing,
      unreadNotificationCount,
      addNotificationListener,
      actionCount,
      addActionCountListener,
      addOfferListListener,
      markNotificationsRead,
      markAllNotificationsRead,
    ]
  )

  return <OfferRealtimeContext.Provider value={value}>{children}</OfferRealtimeContext.Provider>
}

async function fetchUnreadCount(userId: string): Promise<number> {
  try {
    const supabase = getSupabaseBrowserClient()
    const { count } = await supabase
      .from("offer_notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("read", false)
    return typeof count === "number" ? count : 0
  } catch {
    return 0
  }
}
